import '../../../scss/common/components/qpig.scss'

import { Body2, Display3, Grid, GridItem } from '@sainsburys-tech/fable'
import React from 'react'

import { IAdditionalInformation } from '../../types/IAdditionalInformation'
import { IQpigImages } from '../../types/IQpigImages'
import {
  QpigStepTypes,
  QpigTypes,
  StepTypeToImageStepId,
} from '../../types/IQpigTypes'
import QpigImage from './QpigImage'
import QpigPackagingCodeTable from './QpigPackagingCodeTable'
import QpigProcessStepsTable from './QpigProcessStepsTable'
import { hasTableFormatData } from './QpigUtils'

interface IProps {
  additionalInformation: IAdditionalInformation[]
  pluCodes: string[]
  qpigImages: IQpigImages
}

const QpigProcessSteps = (props: IProps) => {
  const { additionalInformation, pluCodes, qpigImages } = props
  const qpigStepTypes = QpigStepTypes
  let qpigNumber = 1

  const getBulletData = (
    qpig: IAdditionalInformation,
    j: string,
    bulletClassName: string,
  ) => (
    <div key={j}>
      <ul style={{ padding: 0, margin: '0 0 16px 16px' }}>
        <li className={bulletClassName}>
          <Body2>
            {qpig.description.substring(
              qpig.description.indexOf(':') + 1,
              qpig.description.length,
            )}
          </Body2>
        </li>
      </ul>
    </div>
  )

  const qpigTables = (qpigStepType: string) => (
    <React.Fragment>
      {qpigStepType === QpigTypes.QpigPackaging && (
        <QpigPackagingCodeTable
          additionalInformationList={additionalInformation.filter(
            (q) => q.type === QpigTypes.QpigPackagingCodes,
          )}
          pluCodes={pluCodes}
        />
      )}
      <QpigProcessStepsTable
        additionalInformationList={additionalInformation.filter(
          (q) => q.type === qpigStepType,
        )}
      />
    </React.Fragment>
  )

  const qpigBulletPoints = (qpigStepType: string) => {
    const bulletPoints = additionalInformation
      .map((qpig: IAdditionalInformation) => {
        const rowId = qpig.description.substring(
          0,
          qpig.description.indexOf(':'),
        )
        if (qpig.type === qpigStepType && !hasTableFormatData([qpig])) {
          const stepsCount = additionalInformation.filter(
            (q) => q.type === qpigStepType,
          ).length
          if (stepsCount > 1) {
            if (
              qpigStepType === QpigTypes.QpigTargetProduct ||
              qpigStepType === QpigTypes.QpigProductShouldBeRejectedIf
            ) {
              return getBulletData(qpig, rowId, '')
            }
            return getBulletData(qpig, rowId, '')
          } else {
            if (
              qpigStepType === QpigTypes.QpigTargetProduct ||
              qpigStepType === QpigTypes.QpigProductShouldBeRejectedIf
            ) {
              return getBulletData(qpig, rowId, '')
            }
            return getBulletData(qpig, rowId, 'c-qpig-no-bullet')
          }
        }
        return null
      })
      .filter(Boolean)

    return bulletPoints
  }

  const stepHasImages = (qpigStepType: string): boolean => {
    if (!qpigImages || !qpigImages.imageFileNames) {
      return false
    }

    const qpigImageStepId = StepTypeToImageStepId[qpigStepType]
    return qpigImages.imageFileNames.some(
      (x) => x.toUpperCase().split('_')[1] === qpigImageStepId.toUpperCase(),
    )
  }

  const getStepImages = (qpigStepType: string): string[] => {
    const qpigImageStepId = StepTypeToImageStepId[qpigStepType]
    return qpigImages.imageFileNames.filter(
      (x) => x.toUpperCase().split('_')[1] === qpigImageStepId.toUpperCase(),
    )
  }

  const getGridColSpan = (qpigStepType: string) => {
    if (stepHasImages(qpigStepType)) {
      return { sm: 8 }
    } else {
      return { sm: 12 }
    }
  }

  const getStepHeader = (qpigStepType: string) => {
    if (
      qpigStepType === QpigTypes.QpigProductShouldBeRejectedIf ||
      qpigStepType === QpigTypes.QpigTargetProduct
    ) {
      return (
        <div style={{ marginBottom: '16px' }}>
          <Display3 style={{ margin: 0, padding: 0 }}>
            {qpigStepType.split('QPIG - ')}
          </Display3>
        </div>
      )
    }
    return (
      <div className='c-qpig-step-header'>
        <span className='c-qpig-step-number'>{qpigNumber++}</span>
        <Display3 className='c-qpig-step-title'>
          {qpigStepType.split('QPIG - ')}
        </Display3>
      </div>
    )
  }

  const renderStepImages = (qpigStepType: string) => {
    return getStepImages(qpigStepType).map(
      (imgFileName: string, index: number) => {
        return (
          <QpigImage
            key={`${imgFileName}-${index}`}
            baseFilePath={qpigImages.baseFilePath}
            imgFileName={imgFileName}
            signature={qpigImages.signature}
          />
        )
      },
    )
  }

  const getQpigSteps = () => {
    if (additionalInformation === null || additionalInformation === undefined) {
      return <div />
    }
    const processSteps = qpigStepTypes.map((qpigStepType, i) => {
      const outerRowId = `${qpigStepType}_outer_${i}`
      if (
        additionalInformation === null ||
        additionalInformation === undefined ||
        !additionalInformation.some((qpig: IAdditionalInformation) =>
          qpig.type.startsWith(qpigStepType),
        )
      ) {
        if (stepHasImages(qpigStepType)) {
          return (
            <div
              key={outerRowId}
              style={{
                marginLeft: '32px',
                marginRight: '32px',
                marginTop: '24px',
              }}
            >
              {getStepHeader(qpigStepType)}
              <Grid>
                <GridItem
                  colSpan={{ xs: 12, sm: 4 }}
                  style={{ marginLeft: '32px' }}
                >
                  <div className='c-qpig-single-image-margin'>
                    {getStepImages(qpigStepType).map(
                      (imgFileName: string, index: number) => {
                        return (
                          <QpigImage
                            key={`${imgFileName}-${index}`}
                            baseFilePath={qpigImages.baseFilePath}
                            imgFileName={imgFileName}
                            signature={qpigImages.signature}
                          />
                        )
                      },
                    )}
                  </div>
                </GridItem>
              </Grid>
            </div>
          )
        } else {
          return <></>
        }
      }
      return (
        <div key={outerRowId} className='c-qpig-step-section'>
          {getStepHeader(qpigStepType)}
          <Grid>
            <GridItem colSpan={{ xs: 12, ...getGridColSpan(qpigStepType) }}>
              <div className='c-table-responsive c-qpig-table-margin'>
                <React.Fragment>
                  {qpigBulletPoints(qpigStepType)}
                </React.Fragment>
              </div>
            </GridItem>
            {stepHasImages(qpigStepType) && (
              <GridItem
                colSpan={{ xs: 12, sm: 4 }}
                className='c-qpig-image-margin'
              >
                {renderStepImages(qpigStepType)}
              </GridItem>
            )}
          </Grid>

          <div className='c-table-responsive c-qpig-table-margin'>
            {qpigTables(qpigStepType)}
          </div>
        </div>
      )
    })
    return processSteps
  }

  return <div className='c-qpig-main-content'>{getQpigSteps()}</div>
}

export default QpigProcessSteps
